import { useMemo, useState } from 'react';

import path from 'path';

import { Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createSearchParams, Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { ConfirmationDialog, useActionBarContribution, type ActionBarAction } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import VectorImage from '~components/media/vectorImage';
import { generalInformation, pagePathSegments, queryParameters } from '~constants';
import { StatusVariant } from '~enums';
import OrganisationsService from '~features/organisations/services/organisationsService';
import { ResourceGroupService } from '~features/resourceGroups';
import { getCommandsForDeviceModel } from '~interfaces/deviceModel';
import ApiError from '~services/apiError';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';
import { decapitalizeString } from '~utils';

import AssignDeviceForm from './forms/assignDeviceForm';
import EditDeviceForm from './forms/editDeviceForm';
import RemoteActionsForm, { DeviceCommand } from './forms/RemoteActionsForm';
import { CommandTypeEnum, getCommandTypeApiValue } from '../../../enums/commandTypeEnum';
import Device from '../../../interfaces/device';
import type { AssignDeviceFormResults } from '../interfaces/AssignDeviceFormResults';
import DevicesService from '../services/devicesService';
import ConnectivityProfileForm, {
	ConnectivityProfileCommand,
} from './forms/connectivityProfileForm';

const deviceService = getService(DevicesService);
const organisationServiceModule = getService(OrganisationsService);
const resourceGroupServiceModule = getService(ResourceGroupService);

export type DeviceActionBarActions =
	| 'assign'
	| 'unassign'
	| 'edit'
	| 'changeResourceGroup'
	| 'remoteAction'
	| 'delete'
	| 'setConnectivityProfile';

interface DeviceActionsBarProps {
	deviceId: string;
	organisationId?: string;
	showUnassignButton?: boolean;
	showAssignButton?: boolean;
	showRemoteActionsButton?: boolean;
	showDeleteButton?: boolean;
	showEditButton?: boolean;
	showChangeResourceGroupButton?: boolean;
	showConnectivityProfileSettingButton?: boolean;
}

const DeviceActionsBar = ({
	deviceId,
	organisationId,
	showUnassignButton = false,
	showAssignButton = false,
	showRemoteActionsButton = false,
	showDeleteButton = true,
	showEditButton = false,
	showChangeResourceGroupButton = false,
	showConnectivityProfileSettingButton = false,
}: DeviceActionsBarProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { palette } = useTheme();

	const [userAction, setUserAction] = useState<DeviceActionBarActions | null>(null);
	const [showModalLoader, setShowModalLoader] = useState<boolean>(false);

	// Load device
	const { data: deviceData, mutate: mutateDevice } = useSWR(
		deviceService.getDeviceByIdCacheKey(deviceId, organisationId),
		([_, id, orgId]) => (id ? deviceService.getDeviceById(id, orgId ?? undefined) : null),
		{
			revalidateOnMount: false,
		}
	);

	const commandOptions = deviceData?.model ? getCommandsForDeviceModel(deviceData?.model) : [];

	const clearUserAction = () => {
		setUserAction(null);
	};

	const sendDeviceCommand = (deviceCommand: DeviceCommand | ConnectivityProfileCommand) => {
		// set form to loading
		setShowModalLoader(true);

		// Get metadata
		let metadata;
		if (deviceCommand.command === CommandTypeEnum.EnableOfficeMode) {
			if (deviceCommand.endDate) {
				metadata = dayjs(deviceCommand.endDate).diff(undefined, 'minutes') + 1;
			}
		} else if (deviceCommand.command === CommandTypeEnum.SetConnectivityProfile) {
			metadata = (deviceCommand as ConnectivityProfileCommand).connectivityProfile ?? undefined;
			if (metadata === undefined) {
				enqueueSnackbar(t('connectivityProfileNotSet', { ns: 'toast' }), { variant: 'error' });
				setShowModalLoader(false);
				return;
			}
		}

		// send command
		const commandTypeApiValue = getCommandTypeApiValue(deviceCommand.command);
		deviceService
			.executeDeviceCommand({
				deviceId: deviceId,
				command: commandTypeApiValue,
				deviceOrganisationId: organisationId,
				metadata: metadata,
			})
			.then(() => {
				// show snackbar
				enqueueSnackbar(t('commandExecutedSuccessfuly', { ns: 'toast' }), {
					variant: 'success',
				});
			})
			.catch((error: ApiError) => {
				// show snackbar
				enqueueSnackbar(error.getDescription(), { variant: 'error' });
			})
			.finally(() => {
				// dismiss dialog
				clearUserAction();
				setShowModalLoader(false);
			});
	};

	const confirmUnassign = async () => {
		setShowModalLoader(true);

		await organisationServiceModule
			.unassignDevices([deviceId])
			.then(() => {
				setUserAction(null);
				navigate(
					{
						pathname: `/${pagePathSegments.Devices}/${deviceId}/${pagePathSegments.General}`,
						search: createSearchParams({
							[queryParameters.OverwrittenClientId]: generalInformation.manufacturingOrganisationId,
						}).toString(),
					},
					{ replace: true }
				);
				enqueueSnackbar(t('deviceUnassignedSuccesfully', { ns: 'toast' }), { variant: 'success' });
			})
			.catch((_reason) => {
				enqueueSnackbar(t('deviceFailedToUnassign', { ns: 'toast' }), {
					variant: 'error',
				});
			})
			.finally(() => {
				setUserAction(null);
				setShowModalLoader(false);
			});
	};

	const confirmDelete = async () => {
		setShowModalLoader(true);
		await deviceService
			.deleteDevice(deviceId)
			.then(() => {
				enqueueSnackbar(t('deviceDeletedSuccesfully', { ns: 'toast' }), { variant: 'success' });
				navigate(`/${pagePathSegments.Devices}`, { replace: true });
			})
			.catch((error: ApiError) => {
				enqueueSnackbar(t('deviceFailedToDelete', { ns: 'toast' }) + ' ' + error.getDescription(), {
					variant: 'error',
				});
			})
			.finally(() => {
				setShowModalLoader(false);
			});
	};

	const handleAssignDevice = async (assignDeviceFormResults: AssignDeviceFormResults) => {
		let assignedToOrganisationSuccessfully = true;

		setShowModalLoader(true);

		if (assignDeviceFormResults.organisationId != undefined) {
			await organisationServiceModule
				.assignDevicesToOrganisation([deviceId], assignDeviceFormResults.organisationId)
				.then(() => {
					if (!assignDeviceFormResults.resourceGroupId) {
						setUserAction(null);
						navigate(
							{
								pathname: `/${pagePathSegments.Devices}/${deviceId}/${pagePathSegments.General}`,
								search: createSearchParams({
									[queryParameters.OverwrittenClientId]:
										assignDeviceFormResults.organisationId ?? '',
								}).toString(),
							},
							{ replace: true }
						);
						enqueueSnackbar(t('deviceAssignedSuccesfully', { ns: 'toast' }), {
							variant: 'success',
						});
					}
				})
				.catch((_reason) => {
					setUserAction(null);
					assignedToOrganisationSuccessfully = false;
					enqueueSnackbar(t('deviceFailedToAssign', { ns: 'toast' }), {
						variant: 'error',
					});
				});
		}

		if (assignDeviceFormResults.resourceGroupId && assignedToOrganisationSuccessfully) {
			await resourceGroupServiceModule
				.assignDevicesToResourceGroup(
					[deviceId],
					assignDeviceFormResults.resourceGroupId,
					assignDeviceFormResults.organisationId
				)
				.then(() => {
					setUserAction(null);
					if (assignDeviceFormResults.organisationId != undefined) {
						navigate(
							{
								pathname: `/${pagePathSegments.Devices}/${deviceId}/${pagePathSegments.General}`,
								search: createSearchParams({
									[queryParameters.OverwrittenClientId]:
										assignDeviceFormResults.organisationId ?? '',
								}).toString(),
							},
							{ replace: true }
						);
					} else {
						mutateDevice();
					}
					enqueueSnackbar(t('deviceAssignedSuccesfully', { ns: 'toast' }), {
						variant: 'success',
					});
				})
				.catch((_reason) => {
					enqueueSnackbar(t('deviceFailedToAssign', { ns: 'toast' }), {
						variant: 'error',
					});
				});
		}
		setShowModalLoader(false);
	};

	const updateDevice = async (device: Device) => {
		setShowModalLoader(true);

		await deviceService
			.updateDevice(device.id, device, organisationId)
			.then((updated) => {
				mutateDevice(updated, { revalidate: false });
				enqueueSnackbar(
					t('successGeneric', {
						ns: 'toast',
						action: t('edited', { ns: 'status' }),
						value: t('device.labels.single', { ns: 'entities' }),
					}),
					{
						variant: 'success',
					}
				);
			})
			.catch((_reason) => {
				enqueueSnackbar(
					t('errorGeneric', {
						ns: 'toast',
						action: t('edited', { ns: 'status' }),
						value: t('device.labels.single', { ns: 'entities' }),
					}),
					{
						variant: 'error',
					}
				);
			})
			.finally(() => {
				clearUserAction();
				setShowModalLoader(false);
			});
	};

	const actions = useMemo<ActionBarAction[]>(() => {
		const list: ActionBarAction[] = [];
		if (showUnassignButton) {
			list.push({
				id: `device-${deviceId}-unassign`,
				label: t('unassign', { ns: 'actions' }),
				icon: <VectorImage name='icon/unlink' />,
				variant: StatusVariant.Destructive,
				onClick: () => setUserAction('unassign'),
			});
		}

		if (showAssignButton) {
			list.push({
				id: `device-${deviceId}-assign`,
				label: t('assign', { ns: 'actions' }),
				icon: <VectorImage name='icon/link' />,
				variant: StatusVariant.Brand,
				onClick: () => setUserAction('assign'),
			});
		}

		if (showEditButton) {
			list.push({
				id: `device-${deviceId}-edit`,
				label: t('edit', { ns: 'actions' }),
				icon: <VectorImage name='icon/edit' />,
				onClick: () => setUserAction('edit'),
			});
		}

		if (showRemoteActionsButton && commandOptions.length > 0) {
			list.push({
				id: `device-${deviceId}-remote-actions`,
				label: t('device.fields.remoteActions', { ns: 'entities' }),
				icon: <VectorImage name='icon/signal' />,
				onClick: () => setUserAction('remoteAction'),
			});
		}

		if (showConnectivityProfileSettingButton) {
			list.push({
				id: `device-${deviceId}-connectivity-profile-setting`,
				label: t('device.labels.connectivityProfile', { ns: 'entities' }),
				icon: <VectorImage name='icon/globe' />,
				onClick: () => setUserAction('setConnectivityProfile'),
			});
		}

		if (showChangeResourceGroupButton) {
			list.push({
				id: `device-${deviceId}-change-resource-group`,
				label: t('changeResourceGroup', { ns: 'actions' }),
				icon: <VectorImage name='icon/swap' />,
				onClick: () => setUserAction('changeResourceGroup'),
			});
		}

		if (showDeleteButton) {
			list.push({
				id: `device-${deviceId}-delete`,
				label: t('delete', { ns: 'actions' }),
				icon: <VectorImage name='icon/bin' />,
				variant: StatusVariant.Destructive,
				onClick: () => setUserAction('delete'),
			});
		}

		return list;
	}, [
		showUnassignButton,
		showAssignButton,
		showEditButton,
		showChangeResourceGroupButton,
		showRemoteActionsButton,
		commandOptions.length,
		showDeleteButton,
		deviceId,
		t,
	]);

	useActionBarContribution(actions, { id: `device-actions-${deviceId}`, overflowLimit: 1 });

	const connectivityProfileWikiDocPath = path.join('/', pagePathSegments.Wiki, '2ddf42499c7c');

	const buttonsHint = useMemo(
		() => (
			<Link
				to={connectivityProfileWikiDocPath}
				target='_blank'
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: Dimens.spacingXs,
					color: palette.textQuaternary.main,
					textDecoration: 'none',
				}}
			>
				<VectorImage name='icon/book-open' width={20} height={20} />
				<Typography variant='labelSm' color={palette.textQuaternary.main} component='span'>
					{t('viewDocs', { ns: 'actions' })}
				</Typography>
			</Link>
		),
		[connectivityProfileWikiDocPath, t]
	);

	return (
		<>
			<ConfirmationDialog
				open={userAction === 'unassign'}
				title={t('device.labels.unassignDeviceDialogTitle', { ns: 'entities' })}
				helperText={t('device.labels.unassignDeviceDialogDescription', { ns: 'entities' })}
				confirmButtonText={t('unassign', { ns: 'actions' })}
				variant={StatusVariant.Destructive}
				icon={<VectorImage name='icon/unlink' />}
				onConfirm={confirmUnassign}
				onClose={clearUserAction}
				loading={showModalLoader}
			/>
			<ConfirmationDialog
				open={userAction === 'delete'}
				title={t('device.labels.deleteDeviceDialogTitle', { ns: 'entities' })}
				helperText={t('device.labels.deleteDeviceDialogDescription', { ns: 'entities' })}
				confirmButtonText={t('delete', { ns: 'actions' })}
				variant={StatusVariant.Destructive}
				icon={<VectorImage name='icon/bin' />}
				onConfirm={confirmDelete}
				onClose={clearUserAction}
				loading={showModalLoader}
			/>
			<FormDialog
				open={userAction === 'remoteAction'}
				title={t('device.fields.remoteActions', { ns: 'entities' })}
				loading={showModalLoader}
				allowContentOverflow
			>
				<RemoteActionsForm
					deviceId={deviceId}
					organisationId={organisationId}
					onSubmit={async (command) => sendDeviceCommand(command)}
					onCancel={clearUserAction}
				/>
			</FormDialog>
			<FormDialog
				open={userAction === 'assign' || userAction === 'changeResourceGroup'}
				title={
					userAction === 'changeResourceGroup' ?
						t('changeResourceGroup', { ns: 'actions' })
					:	t('device.labels.assigDevice', { ns: 'entities' })
				}
				loading={showModalLoader}
			>
				<AssignDeviceForm
					currentlyAssignedOrganisationId={
						userAction === 'changeResourceGroup' ? organisationId : undefined
					}
					showEditOrganisation={userAction === 'assign'}
					onSubmit={(formData) => {
						handleAssignDevice(formData);
					}}
					onCancel={clearUserAction}
				/>
			</FormDialog>
			<FormDialog
				open={userAction === 'edit'}
				title={
					t('edit', { ns: 'actions' }) +
					' ' +
					decapitalizeString(t('device.labels.single', { ns: 'entities' }))
				}
				loading={showModalLoader}
			>
				<EditDeviceForm
					initialDevice={deviceData ?? ({} as Device)}
					onSubmit={async (updated) => updateDevice(updated)}
					onCancel={clearUserAction}
				/>
			</FormDialog>
			<FormDialog
				open={userAction === 'setConnectivityProfile'}
				submitLabel={t('save', { ns: 'actions' })}
				title={t('device.labels.connectivityProfile', { ns: 'entities' })}
				subtitle={t('device.labels.connectivityProfileUpdateInstructions', { ns: 'entities' })}
				buttonsHint={buttonsHint}
				loading={showModalLoader}
				maxWidth={false}
				slotProps={{
					paper: {
						sx: {
							width: { sm: '780px' },
						},
					},
				}}
			>
				<ConnectivityProfileForm
					deviceId={deviceId}
					organisationId={organisationId}
					onSubmit={async (command) => sendDeviceCommand(command)}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default DeviceActionsBar;
